/**
 * Rótulos e cores do título CP → Revenda (#1216). SÓ mapeia o que a API devolveu: o estado, o
 * `vencido`, o saldo e o pago nunca são derivados aqui.
 */
import type { OrigemDaBaixa, TituloEstado } from '../services/titulosCpRevendaApi'

export const ROTULO_DO_ESTADO: Record<TituloEstado, string> = {
  pendente: 'Pendente',
  parcial: 'Parcial',
  quitado: 'Quitado',
  cancelado: 'Cancelado',
}

const COR_DO_ESTADO: Record<TituloEstado, string> = {
  pendente: 'bg-gray-100 text-gray-800',
  parcial: 'bg-amber-100 text-amber-800',
  quitado: 'bg-green-100 text-green-800',
  cancelado: 'bg-slate-200 text-slate-700',
}

export const ROTULO_DO_VENCIDO = 'Vencido'
const COR_DO_VENCIDO = 'bg-red-100 text-red-800'

export const rotuloDoEstado = (estado: string): string =>
  ROTULO_DO_ESTADO[estado as TituloEstado] ?? estado

export const corDoEstado = (estado: string): string =>
  COR_DO_ESTADO[estado as TituloEstado] ?? 'bg-gray-100 text-gray-800'

/** O selo que a tela mostra: `vencido` (da API) vence o estado enquanto houver saldo a receber. */
export const seloDoTitulo = (t: {
  estado: string
  vencido: boolean
}): { rotulo: string; cor: string } =>
  t.vencido
    ? { rotulo: ROTULO_DO_VENCIDO, cor: COR_DO_VENCIDO }
    : { rotulo: rotuloDoEstado(t.estado), cor: corDoEstado(t.estado) }

export const ROTULO_DA_ORIGEM_DA_OBRIGACAO: Record<string, string> = {
  consumo: 'Consumo',
  piso: 'Mensalidade mínima (piso)',
  lote: 'Lote',
  ajuste: 'Ajuste',
  estorno: 'Estorno pró-rata',
  rescisao: 'Rescisão',
}

export const rotuloDaOrigemDaObrigacao = (origem: string): string =>
  ROTULO_DA_ORIGEM_DA_OBRIGACAO[origem] ?? origem

export const ROTULO_DA_ORIGEM_DA_BAIXA: Record<OrigemDaBaixa, string> = {
  manual: 'Baixa no título',
  pagamento: 'Pagamento avulso',
  credito: 'Crédito da revenda',
}

export const rotuloDaOrigemDaBaixa = (origem: string): string =>
  ROTULO_DA_ORIGEM_DA_BAIXA[origem as OrigemDaBaixa] ?? origem

export const FORMAS_DE_BAIXA: { valor: string; rotulo: string }[] = [
  { valor: 'bank_transfer', rotulo: 'Transferência bancária' },
  { valor: 'pix_external', rotulo: 'PIX' },
  { valor: 'boleto_external', rotulo: 'Boleto' },
  { valor: 'cash', rotulo: 'Dinheiro' },
  { valor: 'other', rotulo: 'Outra' },
]

export const rotuloDaForma = (forma: string): string =>
  forma === 'credito_revenda'
    ? 'Crédito da revenda'
    : (FORMAS_DE_BAIXA.find((f) => f.valor === forma)?.rotulo ?? forma)

export const ROTULO_DA_ORIGEM_DO_CREDITO: Record<string, string> = {
  pagamento_excedente: 'Sobra de pagamento',
  titulo_negativo: 'Título negativo',
  uso_em_titulo: 'Usado para abater um título',
  estorno: 'Estorno',
}

export const rotuloDaOrigemDoCredito = (origem: string): string =>
  ROTULO_DA_ORIGEM_DO_CREDITO[origem] ?? origem

/** Valor digitado ("1.234,50" ou "1234.50") → "1234.50" para a API; vazio/inválido → null. */
export const valorParaApi = (texto: string): string | null => {
  let t = texto.trim().replace(/\s/g, '')
  if (!t) return null
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.')
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null
  const n = Number(t)
  return n > 0 ? n.toFixed(2) : null
}
